import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SurveyServiceService } from './survey-service.service';

const STORAGE_KEY = 'display-compliance-v2-export-jobs';

export interface DisplayComplianceExportJobState {
  jobId: string;
  label: string;
  percent: number;
  state: string;
  fileName?: string;
  partCount?: number;
  error?: string;
}

interface StoredJob {
  jobId: string;
  label: string;
  startedAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class DisplayComplianceExportJobService implements OnDestroy {
  private readonly jobsSubject = new BehaviorSubject<DisplayComplianceExportJobState[]>([]);
  readonly jobs$ = this.jobsSubject.asObservable();

  private readonly pollSubs = new Map<string, Subscription>();

  constructor(
    private readonly surveyService: SurveyServiceService,
    private readonly ngZone: NgZone,
  ) {
    this.resumeStoredJobs();
  }

  ngOnDestroy(): void {
    this.pollSubs.forEach((s) => s.unsubscribe());
    this.pollSubs.clear();
  }

  get activeJobs(): DisplayComplianceExportJobState[] {
    return (this.jobsSubject.value ?? []).filter(
      (j) => !['completed', 'failed'].includes(j.state),
    );
  }

  startJob(jobId: string, label: string): void {
    const stored = this.readStorage();
    if (!stored.some((j) => j.jobId === jobId)) {
      stored.push({ jobId, label, startedAt: Date.now() });
      this.writeStorage(stored);
    }
    this.upsertJob({ jobId, label, percent: 1, state: 'waiting' });
    this.pollJob(jobId, label);
  }

  refreshRemoteJobs(): void {
    this.surveyService.listDisplayComplianceExportJobs().subscribe({
      next: (jobs) => {
        const list = jobs ?? [];
        this.ngZone.run(() => {
          const merged = list.map((j) => ({
            jobId: j.jobId,
            label: 'Display Compliance export',
            percent: j.progress ?? 0,
            state: j.state,
            fileName: j.fileName,
            partCount: j.partCount,
            error: j.error,
          }));
          this.jobsSubject.next(merged);
        });
        for (const j of list) {
          if (j.state === 'completed' && j.downloadUrl) {
            this.offerDownload(j.jobId, j.downloadUrl, j.fileName ?? 'display-compliance-export.xlsx');
          }
          if (['completed', 'failed'].includes(j.state)) {
            this.removeStoredJob(j.jobId);
            this.pollSubs.get(j.jobId)?.unsubscribe();
            this.pollSubs.delete(j.jobId);
          } else if (!this.pollSubs.has(j.jobId)) {
            this.pollJob(j.jobId, 'Display Compliance export');
          }
        }
      },
    });
  }

  dismissJob(jobId: string): void {
    this.removeStoredJob(jobId);
    this.pollSubs.get(jobId)?.unsubscribe();
    this.pollSubs.delete(jobId);
    this.jobsSubject.next((this.jobsSubject.value ?? []).filter((j) => j.jobId !== jobId));
  }

  private resumeStoredJobs(): void {
    const stored = this.readStorage();
    for (const j of stored) {
      this.pollJob(j.jobId, j.label);
    }
    if (stored.length > 0) {
      this.refreshRemoteJobs();
    }
  }

  private pollJob(jobId: string, label: string): void {
    this.pollSubs.get(jobId)?.unsubscribe();
    const sub = timer(0, 2000)
      .pipe(switchMap(() => this.surveyService.getDisplayComplianceExportJobStatus(jobId)))
      .subscribe({
        next: (status) => {
          const state = status?.state ?? 'unknown';
          const percent = typeof status?.progress === 'number' ? status.progress : 0;
          this.upsertJob({
            jobId,
            label,
            percent,
            state,
            fileName: status?.fileName,
            partCount: status?.partCount,
            error: status?.error,
          });

          if (state === 'completed' && status?.downloadUrl) {
            this.offerDownload(
              jobId,
              status.downloadUrl,
              status.fileName ?? 'display-compliance-export.xlsx',
            );
            this.removeStoredJob(jobId);
            this.pollSubs.get(jobId)?.unsubscribe();
            this.pollSubs.delete(jobId);
          } else if (state === 'failed') {
            this.removeStoredJob(jobId);
            this.pollSubs.get(jobId)?.unsubscribe();
            this.pollSubs.delete(jobId);
          }
        },
        error: () => {
          this.upsertJob({
            jobId,
            label,
            percent: 0,
            state: 'failed',
            error: 'Could not load export status',
          });
        },
      });
    this.pollSubs.set(jobId, sub);
  }

  private offerDownload(jobId: string, url: string, fileName: string): void {
    const key = `display-compliance-export-downloaded-${jobId}`;
    if (sessionStorage.getItem(key)) {
      return;
    }
    sessionStorage.setItem(key, '1');
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    void this.surveyService.markDisplayComplianceExportDownloaded(jobId).subscribe();
  }

  private upsertJob(job: DisplayComplianceExportJobState): void {
    this.ngZone.run(() => {
      const current = [...(this.jobsSubject.value ?? [])];
      const idx = current.findIndex((j) => j.jobId === job.jobId);
      if (idx >= 0) {
        current[idx] = { ...current[idx], ...job };
      } else {
        current.push(job);
      }
      this.jobsSubject.next(current);
    });
  }

  private readStorage(): StoredJob[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as StoredJob[]) : [];
    } catch {
      return [];
    }
  }

  private writeStorage(jobs: StoredJob[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  }

  private removeStoredJob(jobId: string): void {
    this.writeStorage(this.readStorage().filter((j) => j.jobId !== jobId));
  }
}
